import { useState, useEffect, useCallback } from "react";
import {
  GetPrescriptionTemplates,
  PostPrescriptionTemplate,
  PutPrescriptionTemplate,
  DeletePrescriptionTemplate,
} from "@/services/fhir/prescriptiontemplate";
import { convertBundleToTemplates } from "@/fluxvet/utils/prescriptionTemplateMapper";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";

// =============================================================================
// TIPOS - Estrutura dos modelos de prescrição
// =============================================================================

/**
 * Questionário vinculado ao protocolo veterinário (mesmo contrato usado em
 * `CreateProtocolContent` / `QuestionnairesTabPanel` no fluxo humano).
 * Persistido no QuestionnaireResponse do template via linkId `template-questionnaire`.
 */
export type VetProtocolQuestionnaireItem = {
  type: "questionnaire";
  questionnaireId: string;
  title: string;
  name: string;
  label: string;
  usageType?: string;
};

/**
 * Estrutura de um item de tratamento no modelo
 */
export interface TemplateTreatment {
  name: string;
  dosage: string;
  description: string;
}

/**
 * Estrutura de um item de exame/procedimento no modelo
 */
export interface TemplateExamProcedure {
  description: string;
  notes: string;
}

/**
 * Estrutura de um item de relatório no modelo
 */
export interface TemplateReport {
  content: string;
}

/**
 * Estrutura completa de um modelo de prescrição
 * Preparado para futura conversão em Questionnaire HL7 FHIR R5
 */
export interface PrescriptionTemplate {
  id: string; // UUID único do modelo
  name: string; // Nome do modelo (ex: "Protocolo Dermatite")
  description?: string; // Descrição opcional do modelo
  createdAt: string; // Data de criação (ISO string)
  updatedAt: string; // Data da última atualização (ISO string)

  // Dados das prescrições (arrays podem estar vazios)
  treatments?: TemplateTreatment[];
  examsOrProcedures?: TemplateExamProcedure[];
  reports?: TemplateReport[];
  guidelines?: string; // Orientações gerais
  /** Questionários de pós-consulta (ou outros) incluídos no protocolo. */
  questionnaires?: VetProtocolQuestionnaireItem[];
}

// =============================================================================
// HOOK CUSTOMIZADO
// =============================================================================

/**
 * Hook para gerenciar modelos de prescrição
 * Usa API FHIR (QuestionnaireResponse) para persistência
 */
export const usePrescriptionTemplates = () => {
  const { organization } = useOrganizationContext();
  const { user } = useSessionContext();

  // Estado local com os modelos carregados
  const [templates, setTemplates] = useState<PrescriptionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Carrega os modelos da API FHIR na inicialização
   */
  useEffect(() => {
    const loadTemplates = async () => {
      if (!organization?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const bundle = await GetPrescriptionTemplates({
          organizationId: organization.id,
        });
        const loadedTemplates = convertBundleToTemplates(bundle);
        setTemplates(loadedTemplates);
      } catch (error) {
        console.error("Erro ao carregar modelos de prescrição:", error);
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [organization?.id]);

  /**
   * Cria um novo modelo de prescrição
   * 
   * @param template - Dados do modelo (sem id, createdAt, updatedAt)
   * @returns O modelo criado com id e timestamps
   */
  const createTemplate = useCallback(
    async (template: Omit<PrescriptionTemplate, "id" | "createdAt" | "updatedAt">) => {
      if (!organization?.id || !user?.username) {
        throw new Error("Organização ou usuário não identificado");
      }

      try {
        // Cria no servidor FHIR
        const response = await PostPrescriptionTemplate(
          template,
          organization.id,
          `${oids.cpf}-${user.username}`
        );

        // Converte o QuestionnaireResponse retornado para Template
        const newTemplate: PrescriptionTemplate = {
          id: response.id || `temp-${Date.now()}`,
          name: template.name,
          description: template.description,
          treatments: template.treatments,
          examsOrProcedures: template.examsOrProcedures,
          reports: template.reports,
          guidelines: template.guidelines,
          questionnaires: template.questionnaires,
          createdAt: response.authored || new Date().toISOString(),
          updatedAt: response.meta?.lastUpdated || new Date().toISOString(),
        };

        // Atualiza estado local
        setTemplates((prev) => [...prev, newTemplate]);
        return newTemplate;
      } catch (error) {
        console.error("Erro ao criar modelo:", error);
        throw new Error("Não foi possível criar o modelo. Tente novamente.");
      }
    },
    [organization?.id, user?.username]
  );

  /**
   * Atualiza um modelo existente
   * 
   * @param id - ID do modelo a atualizar
   * @param updates - Dados a atualizar (parcial)
   * @returns O modelo atualizado ou null se não encontrado
   */
  const updateTemplate = useCallback(
    async (id: string, updates: Partial<Omit<PrescriptionTemplate, "id" | "createdAt">>) => {
      if (!organization?.id || !user?.username) {
        throw new Error("Organização ou usuário não identificado");
      }

      const index = templates.findIndex((t) => t.id === id);
      if (index === -1) {
        console.warn(`Modelo com id ${id} não encontrado`);
        return null;
      }

      try {
        // Mescla dados atuais com updates
        const currentTemplate = templates[index];
        const updatedData = {
          name: updates.name ?? currentTemplate.name,
          description: updates.description ?? currentTemplate.description,
          treatments: updates.treatments ?? currentTemplate.treatments,
          examsOrProcedures: updates.examsOrProcedures ?? currentTemplate.examsOrProcedures,
          reports: updates.reports ?? currentTemplate.reports,
          guidelines: updates.guidelines ?? currentTemplate.guidelines,
          questionnaires: updates.questionnaires ?? currentTemplate.questionnaires,
        };

        // Atualiza no servidor FHIR
        const response = await PutPrescriptionTemplate(
          id,
          updatedData,
          organization.id,
          `${oids.cpf}-${user.username}`
        );

        // Cria modelo atualizado
        const updatedTemplate: PrescriptionTemplate = {
          ...currentTemplate,
          ...updatedData,
          updatedAt: response.meta?.lastUpdated || new Date().toISOString(),
        };

        // Atualiza estado local
        setTemplates((prev) => {
          const newTemplates = [...prev];
          newTemplates[index] = updatedTemplate;
          return newTemplates;
        });

        return updatedTemplate;
      } catch (error) {
        console.error("Erro ao atualizar modelo:", error);
        throw new Error("Não foi possível atualizar o modelo. Tente novamente.");
      }
    },
    [templates, organization?.id, user?.username]
  );

  /**
   * Remove um modelo
   * 
   * @param id - ID do modelo a remover
   * @returns true se removido com sucesso, false se não encontrado
   */
  const deleteTemplate = useCallback(
    async (id: string) => {
      const exists = templates.some((t) => t.id === id);
      if (!exists) {
        console.warn(`Modelo com id ${id} não encontrado`);
        return false;
      }

      try {
        // Remove do servidor FHIR
        await DeletePrescriptionTemplate(id);

        // Atualiza estado local
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        return true;
      } catch (error) {
        console.error("Erro ao excluir modelo:", error);
        throw new Error("Não foi possível excluir o modelo. Tente novamente.");
      }
    },
    [templates]
  );

  /**
   * Busca um modelo por ID
   * 
   * @param id - ID do modelo
   * @returns O modelo encontrado ou null
   */
  const getTemplateById = useCallback(
    (id: string) => {
      return templates.find((t) => t.id === id) || null;
    },
    [templates]
  );

  /**
   * Retorna estatísticas sobre um modelo
   * Útil para exibir preview/resumo
   * 
   * @param template - Modelo para analisar
   * @returns Objeto com contadores de itens
   */
  const getTemplateStats = useCallback((template: PrescriptionTemplate) => {
    const questionnairesCount = template.questionnaires?.length || 0;
    return {
      treatmentsCount: template.treatments?.length || 0,
      examsCount: template.examsOrProcedures?.length || 0,
      reportsCount: template.reports?.length || 0,
      questionnairesCount,
      hasGuidelines: !!template.guidelines,
      totalItems:
        (template.treatments?.length || 0) +
        (template.examsOrProcedures?.length || 0) +
        (template.reports?.length || 0) +
        questionnairesCount +
        (template.guidelines ? 1 : 0),
    };
  }, []);

  return {
    // Estado
    templates,
    isLoading,

    // Operações CRUD
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateById,

    // Utilitários
    getTemplateStats,
  };
};

export default usePrescriptionTemplates;
